import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { CookiePreferencesForm } from "@/components/store/CookieBanner";

const CONTENT = {
  delivery: {
    title: "Delivery",
    intro: "We ship from our European warehouse to a growing list of destinations. Checkout confirms the exact rates for your address.",
    sections: [
      ["Where we ship", "European Union, United Kingdom, Switzerland, Norway, United States, Canada, Japan, South Korea, Singapore, Hong Kong, United Arab Emirates and Australia. More destinations are being onboarded."],
      ["Lead time", "Owned stock items ship within one business day of order confirmation. Transit times vary by destination: 2–4 days inside the EU, 4–7 days to the UK and Switzerland, and 5–9 days to the rest of the world."],
      ["Duties & taxes", "EU orders are shipped delivered-duty-paid. For orders outside the EU, duties and taxes are collected at checkout or by the carrier depending on destination; the full landed cost is confirmed before you pay."],
      ["Signature", "Every parcel is tracked and requires a signature on delivery. We do not leave orders unattended."],
    ],
  },
  returns: {
    title: "Returns",
    intro: "We want you to be certain about every purchase. If it isn't right, you can return it within 14 days of delivery.",
    sections: [
      ["How to return", "Request a return from your account within 14 days of delivery. We'll email a prepaid label and instructions. Returns are free from EU destinations; a flat fee is deducted elsewhere."],
      ["Condition", "Items must be unworn, with original tags and packaging. Fine leather, swimwear and underwear have specific conditions — see the item page."],
      ["Refunds", "Refunds are issued to the original payment method within 5 business days of the return being received and inspected."],
      ["Faulty items", "If something arrives damaged, please contact client services within 7 days and we'll arrange a replacement, repair or refund."],
    ],
  },
  privacy: {
    title: "Privacy policy",
    intro: "We hold personal data only to run the store and provide the services you request. We do not sell personal data.",
    sections: [
      ["What we collect", "Account details (name, email, country), order history, delivery addresses, consents, device/session information required for security, and support correspondence."],
      ["Why we collect it", "To process orders, deliver client service, prevent fraud, and — only with your consent — to send marketing or improve the experience through analytics and personalisation."],
      ["How long we keep it", "Account and order records are retained for the period required by applicable commercial law. You may request export or erasure of your account data at any time from your account."],
      ["Your rights", "You may access, rectify, export or erase your personal data from your account, or contact client services. We respond within 30 days."],
    ],
  },
  terms: {
    title: "Terms & conditions",
    intro: "These terms govern purchases on this store. By placing an order you accept these conditions.",
    sections: [
      ["Pricing & currency", "All prices are shown in EUR and include VAT where applicable. The checkout confirms the price, duties and taxes for your destination."],
      ["Payment", "Online checkout is enabled only when a verified payment provider is live. Until then, your bag is saved but orders cannot be placed online."],
      ["Contract", "A contract is formed when we confirm your order by email. We may decline an order if stock is unavailable, pricing is incorrect, or we are unable to ship to your destination."],
      ["Governing law", "These terms are governed by the laws of France. Nothing in these terms limits your consumer rights."],
    ],
  },
  cookies: {
    title: "Cookie preferences",
    intro: "Review and change your cookie choices for this device. Strictly necessary cookies cannot be turned off; everything else is opt-in.",
  },
  checkout: {
    title: "Checkout is coming soon",
    intro: "Online checkout is currently disabled while we finalise the payment provider. Your bag and wishlist are saved on your account.",
    sections: [
      ["What's next", "Checkout, order confirmations and payment capture are delivered in the next development phase. Until then, please contact client services to be notified when ordering goes live."],
    ],
  },
};

export default function Policy({ slug }) {
  const page = CONTENT[slug];
  useSeo(page?.title || "Information");
  if (!page) return <div className="container-lux py-20 text-center"><p className="font-display text-3xl">Page not found</p></div>;
  return (
    <div className="container-lux pt-8 pb-20" data-testid={`policy-${slug}-page`}>
      <nav className="text-xs text-muted-foreground mb-6"><Link to="/" className="hover:text-foreground">Home</Link><span className="mx-1.5">/</span><span className="text-foreground">{page.title}</span></nav>
      <div className="max-w-[760px] mx-auto">
        <h1 className="font-display text-5xl font-medium">{page.title}</h1>
        <p className="text-foreground/80 mt-5 text-lg leading-relaxed">{page.intro}</p>
        {slug === "cookies" ? (
          <div className="mt-10 bg-white border rounded-lg p-6 lg:p-8"><CookiePreferencesForm onSaved={() => { /* banner listens via custom event */ }} /></div>
        ) : (
          <div className="mt-10 space-y-10">
            {page.sections?.map(([h, body]) => (
              <section key={h}><h2 className="font-display text-2xl">{h}</h2><p className="mt-3 text-foreground/80 leading-relaxed">{body}</p></section>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-14">Last updated: January 2026</p>
      </div>
    </div>
  );
}
